import { useEffect, useRef, useState } from "react";
import "./_anchor-tabs.scss";

/**
 * Scroll-spy pill nav for a single continuously-scrolling detail page (the
 * Liquid Glass design's Details/Contacts/Billing/Activity pattern) -
 * distinct from `Tabs`, which switches which content is *mounted*. Every
 * section here is always mounted; clicking a pill scrolls to it, and
 * scrolling manually moves the active pill to whichever section is
 * currently at the top of the viewport.
 *
 * Rooted at `scrollContainerRef` rather than the window - these detail
 * pages scroll their own inner `__scroll` div, not the page/body.
 *
 * The caller owns rendering the sections themselves; each section wrapper
 * needs `id={`${idPrefix}-${key}`}` and `data-anchor-key={key}` matching
 * one entry in `sections` for the observer to find it.
 */
const AnchorTabs = ({ sections = [], idPrefix, scrollContainerRef, className = "" }) => {
  const [activeKey, setActiveKey] = useState(sections[0]?.key);
  const activeKeyRef = useRef(activeKey);
  activeKeyRef.current = activeKey;
  const suppressObserverRef = useRef(false);
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef(null);

  // A zero-height sibling rendered immediately above this bar - once it
  // scrolls out of the container's view, this bar (position: sticky, see
  // _anchor-tabs.scss) is the thing now pinned at the top, so that's the
  // signal used to only apply the header-matching background/divider
  // while actually stuck, not while scrolling normally with the page.
  useEffect(() => {
    const root = scrollContainerRef?.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;

    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
      root,
      threshold: 0,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollContainerRef]);

  useEffect(() => {
    const root = scrollContainerRef?.current;
    if (!root || sections.length === 0) return undefined;

    const elements = sections
      .map((section) => document.getElementById(`${idPrefix}-${section.key}`))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        if (suppressObserverRef.current) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const key = visible[0]?.target?.dataset?.anchorKey;
        if (key && key !== activeKeyRef.current) setActiveKey(key);
      },
      // Only the top ~30% band of the scroll container counts as "current" -
      // otherwise a tall section stays "active" long after scrolling past it.
      { root, rootMargin: "0px 0px -70% 0px", threshold: [0, 1] }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections, idPrefix, scrollContainerRef]);

  useEffect(() => {
    const root = scrollContainerRef?.current;
    const lastKey = sections[sections.length - 1]?.key;
    if (!root || !lastKey) return undefined;

    // Safety net for the last section: if it's shorter than the container's
    // "top band" cutoff above, its own top may never cross into that band,
    // so the observer alone would never mark it active even once you've
    // scrolled all the way down to it.
    const handleScroll = () => {
      if (suppressObserverRef.current) return;
      const atBottom = root.scrollHeight - root.scrollTop - root.clientHeight < 4;
      if (atBottom && lastKey !== activeKeyRef.current) setActiveKey(lastKey);
    };

    root.addEventListener("scroll", handleScroll, { passive: true });
    return () => root.removeEventListener("scroll", handleScroll);
  }, [sections, scrollContainerRef]);

  const handleClick = (key) => {
    const root = scrollContainerRef?.current;
    const el = document.getElementById(`${idPrefix}-${key}`);
    if (!root || !el) return;

    setActiveKey(key);
    // Ignore the observer's own updates for the duration of the smooth
    // scroll - a section that briefly crosses the "top band" mid-scroll
    // would otherwise flicker the active pill before landing on the target.
    suppressObserverRef.current = true;
    const targetTop = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
    root.scrollTo({ top: targetTop, behavior: "smooth" });
    window.setTimeout(() => {
      suppressObserverRef.current = false;
    }, 600);
  };

  return (
    // The sentinel is a real sibling flex item of `.anchor-tabs` (both
    // direct children of `__scroll`) rather than wrapped together -
    // `.anchor-tabs` needs `__scroll` itself as its sticky containing
    // block (which has plenty of height below to give it room to actually
    // stay stuck through the rest of the scroll); a tightly-fit wrapper
    // around just these two elements would become that containing block
    // instead, with zero slack, so the bar would never visibly stick at
    // all. The sentinel's own gap contribution to `__scroll`'s `flex gap`
    // is cancelled directly in its own CSS instead (see
    // .anchor-tabs__sentinel's negative margin).
    <>
      <div ref={sentinelRef} className="anchor-tabs__sentinel" aria-hidden="true" />
      <div className={`anchor-tabs ${isStuck ? "anchor-tabs--stuck" : ""} ${className}`.trim()}>
        {sections.map((section) => (
          <button
            key={section.key}
            type="button"
            className={`anchor-tabs__pill${activeKey === section.key ? " anchor-tabs__pill--active" : ""}`}
            onClick={() => handleClick(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
    </>
  );
};

export default AnchorTabs;
